<template>
  <div>
    <ReCaptchaErrorDialog v-if="hasReCaptchaError" />
    <div class="content">
      <!--
        Present just to fulfill the purpose of having a label for login section and to be complaint with WCAG requirements
      -->
      <h1 aria-hidden="true" style="display: none">Login in TubeWay</h1>

      <LoginLogo />
      <VContainer>
        <VForm
          aria-label="TubeWay login"
          ref="form"
          :disabled="formSubmitted"
          v-on:submit.prevent="login"
          @focusin="warmUpReCaptcha"
          @keydown="warmUpReCaptcha"
          @pointerdown="warmUpReCaptcha"
          data-cy="login-form"
        >
          <VRow v-if="warningMessage">
            <VCol>
              <div class="d-flex align-center">
                <VIcon color="orange" class="mr-2">mdi-alert</VIcon>
                <span class="text-orange">{{ warningMessage }}</span>
              </div>
            </VCol>
          </VRow>

          <VRow>
            <VCol>
              <VTextField
                v-model="email"
                v-bind:rules="emailRules"
                v-bind:error-messages="emailErrors"
                @update:model-value="emailErrors = []"
                placeholder="E-Mail"
                prepend-inner-icon="mdi-account"
                density="comfortable"
                variant="solo-filled"
                flat
                block
                class="login-input"
                required
                data-cy="login-email"
              />
            </VCol>
          </VRow>

          <VRow>
            <VCol>
              <VTextField
                type="password"
                placeholder="Password"
                v-model="password"
                v-bind:rules="passwordRules"
                prepend-inner-icon="mdi-lock"
                density="comfortable"
                variant="solo-filled"
                flat
                block
                class="login-input"
                required
                data-cy="login-password"
              />
            </VCol>
          </VRow>

          <VRow v-if="temporaryPasswordExpired">
            <VCol>
              <VAlert color="red" variant="outlined" text type="error">
                Your temporary password has expired. You can easily reset it on our <a href="/account/reset-temporary-password">password reset page</a>.
              </VAlert>
            </VCol>
          </VRow>

          <VRow>
            <VCol>
              <VBtn
                id="login-button"
                type="submit"
                label="Login"
                :disabled="formSubmitted"
                :loading="formSubmitted"
                class="bg-primary mt-4"
                block
                data-cy="login-signin-btn"
              >
                Login
              </VBtn>
            </VCol>
          </VRow>

          <VRow>
            <VCol>
              <VBtn
                id="login-with-google"
                label="Login with Google"
                @click="loginWithGoogle"
                type="button"
                elevation="0"
                color="secondaryButtons"
                class="mt-4"
                block
              >
                <img
                  alt=""
                  :src="googleLogo"
                  height="24"
                  width="24"
                  class="mr-2"
                />
                Login with Google
              </VBtn>
            </VCol>
          </VRow>
        </VForm>

        <EnterpriseLogin v-if="$isAdobeExtension() || $isFCPExtension()" />

        <VRow class="mb-4">
          <VCol cols="6">

            <!-- US #12434 - Temporary disable account creation -->
            <!-- <div class="text-left mt-3">
              New user?
              <router-link to="/account-create">Register</router-link>
            </div> -->

            <div class="text-left mt-3">
              <router-link to="/account/policy">Privacy policy</router-link>
            </div>
          </VCol>
          <VCol cols="6">
            <div class="text-right mt-3">
              <router-link to="/account/forgot-password">Reset password</router-link>
            </div>
          </VCol>
        </VRow>

        <VRow>
          <VCol cols="12">
            <div>For account requests or login issues, contact us at <a href="mailto:info@tubeway.io">info@tubeway.io</a> or
              call <a href="tel:+31353033266">+31 35 30 33 266</a>.</div>
            <div class="mt-2">We're here to assist you!</div>
          </VCol>
        </VRow>
      </VContainer>

      <!-- <div class="text-center mt-3">
        // Add some links out of the box here
        <router-link to="/account/forgot-password">Forgot password?</router-link>
      </div> -->

    </div>

  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import googleLogo from '@/assets/images/google-logo.svg';
import CognitoRedirectingMixin from './CognitoRedirectingMixin.js';
import EnterpriseLogin from '@/routes/account/components/EnterpriseLogin.vue';
import LoginLogo from './components/LoginLogo.vue';
import ReCaptchaErrorDialog from '@/routes/account/components/ReCaptchaErrorDialog.vue';
import Helper from '@/components/helper.js';

export default {
  name: 'AccountLogin',
  mixins: [CognitoRedirectingMixin],
  components: {
    ReCaptchaErrorDialog,
    LoginLogo,
    EnterpriseLogin,
  },
  data: function() {
    return {
      googleLogo,

      email: "",
      emailErrors: [],
      emailRules: [
        v => !!v || 'Please, enter email address.',
        v => Helper.isEmailValid(v) || 'Please, enter valid email address.',
      ],
      password: "",
      passwordRules: [
        v => !!v || this.$t('menus.createUser.passwordRequired'),
        v => (v && v.length >= 8) || this.$t('menus.createUser.passwordLength')
      ],

      temporaryPasswordExpired: false,
      hasReCaptchaError: false,
      formSubmitted: false,
      warningMessage: null,
      recaptchaLoadPromise: null,
    };
  },
  mounted: async function() {
    this.$recaptchaOnError(() => {
      this.hasReCaptchaError = true;
    });

    // Get the value about an error from URL the parameter.
    if (this.$route?.query?.error === 'sessionExpired') {
      this.warningMessage = 'Your session has expired. Please log in again.';
    } else if (this.$route?.query?.error === 'accountNotFound') {
      this.emailErrors = ['User & password combination does not exist.'];
    }
  },
  beforeUnmount: async function() {
    this.$recaptchaHideBadge();
  },
  methods: {
    loadReCaptcha: async function() {
      if (!this.recaptchaLoadPromise) {
        this.recaptchaLoadPromise = this.$recaptchaLoaded()
          .then(() => {
            this.$recaptchaShowBadge();
          })
          .catch((error) => {
            this.recaptchaLoadPromise = null;
            this.hasReCaptchaError = true;
            throw error;
          });
      }

      return this.recaptchaLoadPromise;
    },
    warmUpReCaptcha: function() {
      this.loadReCaptcha().catch(() => {});
    },
    login: async function() {
      this.temporaryPasswordExpired = false;

      const validForm = await this.$refs.form.validate();

      if (!validForm.valid) {
        return;
      }

      this.emailErrors = [];
      this.formSubmitted = true;

      try {
        await this.loadReCaptcha();

        // Execute reCAPTCHA with action "login" immediately before submitting.
        const captchaToken = await this.$recaptcha('login');

        const loginParams = {
          username: this.email,
          password: this.password,
          options: {
            clientMetadata: {
              captcha: captchaToken,
            },
          },
        };

        const authStore = useAuthStore();
        let cognitoResponse = await authStore.signIn(loginParams);
        this.cognitoRedirectingMixin(cognitoResponse, this.email);
      } catch (e) {
        if (e?.code === 'NotAuthorizedException') {
          const searchText = "temporary password has expired";
          const regex = new RegExp(searchText, 'i');

          if (regex.test(e?.message)) {
            this.temporaryPasswordExpired = true;
          }
        }

        if (this.hasReCaptchaError && !e?.code) {
          return;
        }

        this.emailErrors = ['User & password combination does not exist.'];
      } finally {
        this.formSubmitted = false;
      }
    },
    async loginWithGoogle() {
      const authStore = useAuthStore();
      await authStore.signInWithProvider({ provider: 'Google' });
    },
  },
  computed: {
  },
}
</script>

<style>
#login-button:focus {
  outline-width: 1px;
  outline-style: auto;
  outline-offset: 3px;
}

#login-with-google:focus {
  outline-width: 1px;
  outline-style: auto;
  outline-offset: 3px;
}

#enterprise-login:focus {
  outline-width: 1px;
  outline-style: auto;
  outline-offset: 3px;
}

#login-layout .login-input :deep(.v-field) {
  background-color: rgb(var(--v-theme-surface));
}

#login-layout .login-input :deep(.v-field__input),
#login-layout .login-input :deep(.v-label),
#login-layout .login-input :deep(input),
#login-layout .login-input :deep(textarea) {
  color: rgb(var(--v-theme-on-surface));
  caret-color: rgb(var(--v-theme-on-surface));
  -webkit-text-fill-color: rgb(var(--v-theme-on-surface));
}

#login-layout .login-input :deep(input:-webkit-autofill),
#login-layout .login-input :deep(input:-webkit-autofill:hover),
#login-layout .login-input :deep(input:-webkit-autofill:focus),
#login-layout .login-input :deep(input:-webkit-autofill:active) {
  -webkit-box-shadow: 0 0 0 1000px rgb(var(--v-theme-surface)) inset !important;
  box-shadow: 0 0 0 1000px rgb(var(--v-theme-surface)) inset !important;
  -webkit-text-fill-color: rgb(var(--v-theme-on-surface)) !important;
}
</style>
